// Bots: Liste, anlegen (Token einmalig), bearbeiten, Avatar, zu Server hinzufügen, löschen, Bot-Tokens.
import type { ApiTokenCreatedDto, BotDto } from '@lagerfeuer/shared';
import { FIXED_LIMITS } from '@lagerfeuer/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { botsApi, lookupApi } from '../api';
import { displayName, formatDateTime } from '../format';
import { toast } from '../toast';
import { Button, ConfirmButton, Dialog, Empty, ErrorBox, Field, Loading, SecretReveal } from '../ui';
import { BOT_USERNAME_RE, botsErrorText, botsKey, IMAGE_ACCEPT, isAllowedImage, serversLookupKey } from './bots-utils';
import { TokenCreateDialog, TokenList } from './TokensSection';
import { sameOriginUrl } from '../safe-url';

function BotAvatar(props: { bot: BotDto }) {
  const u = props.bot.user;
  const src = sameOriginUrl(u.avatarUrl);
  if (src) return <img className="lfx-bt-avatar" src={src} alt="" data-testid="bots-bot-avatar" />;
  return (
    <span className="lfx-bt-avatar lfx-bt-avatar--empty" aria-hidden="true" data-testid="bots-bot-avatar">
      {displayName(u).slice(0, 1).toUpperCase()}
    </span>
  );
}

function BotCreateDialog(props: { onClose: () => void }) {
  const qc = useQueryClient();
  const [username, setUsername] = useState('');
  const [name, setName] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ bot: BotDto; token: ApiTokenCreatedDto } | null>(null);
  const create = useMutation({
    gcTime: 0, // Einmal-Geheimnis nicht im Mutation-Cache liegen lassen
    mutationFn: botsApi.create,
    onSuccess: (res) => {
      setCreated(res);
      void qc.invalidateQueries({ queryKey: botsKey });
      void qc.invalidateQueries({ queryKey: ['extras', 'tokens'] });
    },
  });

  if (created) {
    return (
      <Dialog
        title="Bot angelegt"
        testId="bots-created"
        onClose={props.onClose}
        footer={
          <Button variant="primary" testId="bots-created-done" onClick={props.onClose}>
            Fertig
          </Button>
        }
      >
        <p>
          <strong>{displayName(created.bot.user)}</strong> (@{created.bot.user.username}) ist bereit. Der Token hat Vollzugriff (<code>*</code>) im Namen des
          Bots.
        </p>
        <SecretReveal label="Bot-Token" value={created.token.token} testId="bots-created-token" />
        <pre className="lfx-bt-code">{`Authorization: Bot ${created.token.token}`}</pre>
      </Dialog>
    );
  }

  return (
    <Dialog title="Bot anlegen" testId="bots-create-dialog" onClose={props.onClose}>
      <form
        className="lfx-stack"
        data-testid="bots-create-form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          setFormError(null);
          if (!BOT_USERNAME_RE.test(username)) {
            setFormError('Benutzername: 2–32 Zeichen, nur Kleinbuchstaben, Ziffern, „_" und „.".');
            return;
          }
          create.mutate({ username, ...(name.trim() ? { displayName: name.trim() } : {}) });
        }}
      >
        <Field label="Benutzername" hint="2–32 Zeichen: a–z, 0–9, _ und .">
          {(id) => (
            <input
              id={id}
              className="lfx-input"
              data-testid="bots-create-username"
              value={username}
              autoComplete="off"
              onChange={(e) => setUsername(e.target.value.toLowerCase())}
            />
          )}
        </Field>
        <Field label="Anzeigename (optional)">
          {(id) => (
            <input
              id={id}
              className="lfx-input"
              data-testid="bots-create-displayname"
              value={name}
              maxLength={FIXED_LIMITS.displayNameMax}
              onChange={(e) => setName(e.target.value)}
            />
          )}
        </Field>
        {formError && (
          <div className="lfx-error" role="alert" data-testid="bots-create-form-error">
            {formError}
          </div>
        )}
        {create.isError && (
          <div className="lfx-error" role="alert" data-testid="bots-create-error">
            {botsErrorText(create.error, 'bot')}
          </div>
        )}
        <div className="lfx-row lfx-bt-actions">
          <Button variant="ghost" testId="bots-create-cancel" onClick={props.onClose}>
            Abbrechen
          </Button>
          <Button type="submit" variant="primary" testId="bots-create-submit" disabled={create.isPending}>
            {create.isPending ? 'Lege an …' : 'Bot anlegen'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function BotEditDialog(props: { bot: BotDto; onClose: () => void }) {
  const qc = useQueryClient();
  const u = props.bot.user;
  const [username, setUsername] = useState(u.username);
  const [name, setName] = useState(u.displayName ?? '');
  const [bio, setBio] = useState(u.bio ?? '');
  const [formError, setFormError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (body: Parameters<typeof botsApi.update>[1]) => botsApi.update(u.id, body),
    onSuccess: () => {
      toast({ kind: 'success', title: 'Bot gespeichert' });
      void qc.invalidateQueries({ queryKey: botsKey });
      props.onClose();
    },
  });
  return (
    <Dialog title={`Bot bearbeiten: ${displayName(u)}`} testId="bots-edit-dialog" onClose={props.onClose}>
      <form
        className="lfx-stack"
        data-testid="bots-edit-form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          setFormError(null);
          if (!BOT_USERNAME_RE.test(username)) {
            setFormError('Benutzername: 2–32 Zeichen, nur Kleinbuchstaben, Ziffern, „_" und „.".');
            return;
          }
          const body: Parameters<typeof botsApi.update>[1] = {};
          if (username !== u.username) body.username = username;
          if (name.trim() !== (u.displayName ?? '')) body.displayName = name.trim() || null;
          if (bio !== (u.bio ?? '')) body.bio = bio.trim() ? bio : null;
          if (!Object.keys(body).length) return props.onClose();
          save.mutate(body);
        }}
      >
        <Field label="Benutzername">
          {(id) => (
            <input id={id} className="lfx-input" data-testid="bots-edit-username" value={username} onChange={(e) => setUsername(e.target.value.toLowerCase())} />
          )}
        </Field>
        <Field label="Anzeigename">
          {(id) => (
            <input
              id={id}
              className="lfx-input"
              data-testid="bots-edit-displayname"
              value={name}
              maxLength={FIXED_LIMITS.displayNameMax}
              onChange={(e) => setName(e.target.value)}
            />
          )}
        </Field>
        <Field label="Über mich">
          {(id) => <textarea id={id} className="lfx-textarea" data-testid="bots-edit-bio" value={bio} onChange={(e) => setBio(e.target.value)} />}
        </Field>
        {formError && (
          <div className="lfx-error" role="alert" data-testid="bots-edit-form-error">
            {formError}
          </div>
        )}
        {save.isError && (
          <div className="lfx-error" role="alert" data-testid="bots-edit-error">
            {botsErrorText(save.error, 'bot')}
          </div>
        )}
        <div className="lfx-row lfx-bt-actions">
          <Button variant="ghost" testId="bots-edit-cancel" onClick={props.onClose}>
            Abbrechen
          </Button>
          <Button type="submit" variant="primary" testId="bots-edit-submit" disabled={save.isPending}>
            Speichern
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function BotCard(props: { bot: BotDto }) {
  const qc = useQueryClient();
  const { bot } = props;
  const id = bot.user.id;
  const [editing, setEditing] = useState(false);
  const [tokensOpen, setTokensOpen] = useState(false);
  const [tokenDialog, setTokenDialog] = useState(false);
  const [serverId, setServerId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const servers = useQuery({ queryKey: serversLookupKey, queryFn: lookupApi.servers });
  const onErr = (what: 'bot' | 'server') => (err: unknown) => setError(botsErrorText(err, what));
  const refresh = () => void qc.invalidateQueries({ queryKey: botsKey });

  const avatar = useMutation({
    mutationFn: (file: File) => botsApi.setAvatar(id, file),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: onErr('bot'),
  });
  const removeAvatar = useMutation({
    mutationFn: () => botsApi.deleteAvatar(id),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: onErr('bot'),
  });
  const addToServer = useMutation({
    mutationFn: (sid: string) => botsApi.addToServer(id, sid),
    onSuccess: (_r, sid) => {
      setError(null);
      const name = servers.data?.find((s) => s.id === sid)?.name ?? 'Server';
      toast({ kind: 'success', title: `${displayName(bot.user)} ist „${name}" beigetreten` });
    },
    onError: onErr('server'),
  });
  const remove = useMutation({
    mutationFn: () => botsApi.remove(id),
    onSuccess: () => {
      toast({ kind: 'success', title: 'Bot gelöscht' });
      refresh();
      void qc.invalidateQueries({ queryKey: ['extras', 'tokens'] });
    },
    onError: onErr('bot'),
  });

  const tokensRegion = `lfx-bt-tokens-${id}`;
  return (
    <li className="lfx-card lfx-stack lfx-bt-bot" data-testid="bots-bot-row" data-bot-id={id}>
      <div className="lfx-row">
        <BotAvatar bot={bot} />
        <div className="lfx-grow">
          <div className="lfx-bt-name" data-testid="bots-bot-name">
            {displayName(bot.user)}
          </div>
          <div className="lfx-muted" data-testid="bots-bot-username">
            @{bot.user.username} · angelegt {formatDateTime(bot.createdAt)}
          </div>
        </div>
        <Button testId={`bots-bot-edit-${id}`} onClick={() => setEditing(true)}>
          Bearbeiten
        </Button>
        <ConfirmButton label="Löschen" confirmLabel="Bot wirklich löschen?" testId={`bots-bot-delete-${id}`} disabled={remove.isPending} onConfirm={() => remove.mutate()} />
      </div>
      {bot.user.bio && <p className="lfx-muted lfx-bt-bio">{bot.user.bio}</p>}

      <div className="lfx-row">
        <label className="lfx-btn lfx-btn--ghost lfx-bt-file">
          {avatar.isPending ? 'Lade hoch …' : 'Avatar hochladen'}
          <input
            type="file"
            accept={IMAGE_ACCEPT}
            className="lfx-bt-file__input"
            data-testid={`bots-bot-avatar-input-${id}`}
            disabled={avatar.isPending}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (!file) return;
              if (!isAllowedImage(file)) {
                setError('Nur Bilder im Format PNG, JPEG, GIF oder WebP.');
                return;
              }
              avatar.mutate(file);
            }}
          />
        </label>
        {bot.user.avatarUrl && (
          <Button variant="ghost" testId={`bots-bot-avatar-remove-${id}`} disabled={removeAvatar.isPending} onClick={() => removeAvatar.mutate()}>
            Avatar entfernen
          </Button>
        )}
      </div>

      <div className="lfx-row" role="group" aria-label="Zu Server hinzufügen">
        <select
          className="lfx-select lfx-bt-server-select"
          aria-label="Server wählen"
          data-testid={`bots-bot-server-select-${id}`}
          value={serverId}
          onChange={(e) => setServerId(e.target.value)}
        >
          <option value="">Server wählen …</option>
          {(servers.data ?? []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
        <Button testId={`bots-bot-server-add-${id}`} disabled={!serverId || addToServer.isPending} onClick={() => addToServer.mutate(serverId)}>
          Zu Server hinzufügen
        </Button>
        <span className="lfx-field__hint">Benötigt im Server das Recht „Server verwalten".</span>
      </div>

      {error && (
        <div className="lfx-error" role="alert" data-testid={`bots-bot-error-${id}`}>
          {error}
        </div>
      )}

      <div>
        <button
          type="button"
          className="lfx-bt-toggle"
          aria-expanded={tokensOpen}
          aria-controls={tokensRegion}
          data-testid={`bots-bot-tokens-toggle-${id}`}
          onClick={() => setTokensOpen((v) => !v)}
        >
          <span aria-hidden="true">{tokensOpen ? '▾' : '▸'}</span> Tokens dieses Bots
        </button>
        {tokensOpen && (
          <div id={tokensRegion} className="lfx-stack lfx-bt-subtokens">
            <TokenList ownerId={id} testId={`bots-bot-tokens-${id}`} />
            <div>
              <Button testId={`bots-bot-token-create-${id}`} onClick={() => setTokenDialog(true)}>
                Weiteren Token erstellen
              </Button>
            </div>
          </div>
        )}
      </div>

      {editing && <BotEditDialog bot={bot} onClose={() => setEditing(false)} />}
      {tokenDialog && <TokenCreateDialog fixedOwner={bot} onClose={() => setTokenDialog(false)} />}
    </li>
  );
}

/** Abschnitt „Bots". */
export function BotsSection() {
  const [creating, setCreating] = useState(false);
  const q = useQuery({ queryKey: botsKey, queryFn: botsApi.list });
  return (
    <section className="lfx-card lfx-stack" data-testid="bots-section" aria-labelledby="lfx-bt-bots-title">
      <div className="lfx-row lfx-row--between">
        <h2 id="lfx-bt-bots-title" className="lfx-bt-title">
          Bots
        </h2>
        <Button variant="primary" testId="bots-create" onClick={() => setCreating(true)}>
          Bot anlegen
        </Button>
      </div>
      <p className="lfx-muted lfx-bt-note">Bots sind eigene Konten, die du über die API steuerst. Beim Anlegen erhältst du einmalig einen Token.</p>
      {q.isPending ? (
        <Loading testId="bots-loading" />
      ) : q.isError ? (
        <ErrorBox error={q.error} testId="bots-error" />
      ) : q.data.length === 0 ? (
        <Empty testId="bots-empty">Du hast noch keine Bots.</Empty>
      ) : (
        <ul className="lfx-bt-list" data-testid="bots-list">
          {q.data.map((b) => (
            <BotCard key={b.user.id} bot={b} />
          ))}
        </ul>
      )}
      {creating && <BotCreateDialog onClose={() => setCreating(false)} />}
    </section>
  );
}
